import React from "react";
import { Link } from "react-router-dom";

function Home() {
  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <section className="text-center py-12 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
        <h1 className="text-4xl font-bold text-gray-800 mb-6">
          Welcome to PlasAnn
        </h1>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8 leading-relaxed">
          PlasAnn is a comprehensive web-based and command-line plasmid annotation platform powered by a manually curated plasmid database sourced from peer-reviewed literature. The platform helps researchers identify coding sequences, resistance genes, mobile genetic elements, and origin of replication regions from plasmid DNA sequences by leveraging this carefully maintained dataset. By integrating multiple bioinformatics tools with high-quality, citation-backed reference data, PlasAnn provides a streamlined analysis pipeline that is especially valuable for studies in microbial genomics, antimicrobial resistance, and horizontal gene transfer.
        </p>
        <div className="flex justify-center space-x-4">
          <Link
            to="/analysis"
            className="bg-[#538249] text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors"
          >
            Start Analysis
          </Link>
          <Link
            to="/database"
            className="bg-white text-[#538249] border-2 border-[#538249] px-8 py-3 rounded-lg text-lg font-semibold hover:bg-green-50 transition-colors"
          >
            Browse Database
          </Link>
        </div>
      </section>

      {/* Annotation Pipeline Section */}
      <section className="py-8">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Annotation Pipeline
        </h2>
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <img 
            src="/pipeline-diagram.png" 
            alt="PlasAnn Annotation Pipeline" 
            className="max-w-full h-auto mx-auto rounded-lg"
          />
          
        </div>
      </section>

      {/* Key Features */}
      <section className="py-8">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Key Features
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="text-4xl mb-4">🧬</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Coding Sequence Detection
            </h3>
            <p className="text-gray-600">
              Automated identification of protein-coding regions using Prodigal
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="text-4xl mb-4">🛡️</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Resistance Gene Analysis
            </h3>
            <p className="text-gray-600">
              Comprehensive detection of antimicrobial resistance markers
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="text-4xl mb-4">🔄</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Mobile Elements
            </h3>
            <p className="text-gray-600">
              Detection of transposons, origins of transfer, and replication
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="text-4xl mb-4">🗺️</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Visual Mapping
            </h3>
            <p className="text-gray-600">
              Generate clear plasmid maps from annotated sequences
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-8 bg-gray-50 rounded-lg">
        <div className="px-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
            Research Applications
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Microbial Genomics
              </h3>
              <p className="text-gray-600">
                Comprehensive plasmid characterization for genomic studies and comparative analysis
              </p>
            </div>
            
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Antimicrobial Resistance
              </h3>
              <p className="text-gray-600">
                Track resistance gene distribution and evolution across bacterial populations
              </p>
            </div>
            
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Horizontal Gene Transfer
              </h3>
              <p className="text-gray-600">
                Study mobile genetic elements and their role in bacterial adaptation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section className="py-8 text-center">
        <h2 className="text-3xl font-bold text-gray-800 mb-6">
          Ready to Get Started?
        </h2>
        <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
          Choose between our web-based interface for quick analysis or download the command-line tool for batch processing and integration into your workflows.
        </p>
        <div className="flex justify-center space-x-6">
          <Link
            to="/analysis"
            className="bg-[#538249] text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
          >
            Web Analysis
          </Link>
          <Link
            to="/download"
            className="bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
          >
            Download Tool
          </Link>
          <Link
            to="/installation"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Installation Guide
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Home;